// src/services/liabilityService.ts
import { API_CONFIG } from '../config/apiConfig';

// --- INTERFACES ---
// This interface is now aligned with your backend's camelCase structure
export interface LiabilityData {
  id: string; // The local UUID from your Node.js backend
  liabilityOwnerType: string;
  liabilityType: string;
  liabilitySubType: string;
  liabilityStartDate: string;
  liabilityEndDate: string;
  liabilityTenure: string;
  liabilityTotalAmount: string;
  liabilityOutstandingAmount: string;
  liabilityInterestRate: string;
  liabilityInstalmentAmount: string;
  liabilityFrequency: string;
  status?: string;
  familyMemberId?: string;
  jogetId?: string; // Optional: To store the original Joget PK
}

// Defines the paginated response structure from your Node.js backend
export interface liabilityApiResponse {
  status: string;
  data: LiabilityData[];
  pagination: {
    total: number;
    size: number;
    currentPage: number;
  };
}

// --- AUTHENTICATION HELPER ---
// Standardized helper to get the JWT Bearer token
const getAuthToken = (): string | null => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.error('Authentication Error: JWT Token not found in localStorage.');
    return null;
  }
  return token;
};

// --- API FUNCTIONS ---

/**
 * Fetches all liability records for the logged-in user from your Node.js backend.
 */
export const fetchLiabilityData = async (page = 1, size = 10): Promise<liabilityApiResponse> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/liabilities?page=${page}&size=${size}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(errorBody.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();

  } catch (error) {
    console.error('Error fetching liability data:', error);
    throw error;
  }
};


/**
 * Creates or updates a liability record via your Node.js backend.
 */
export const saveOrUpdateLiabilityRecord = async (liability: LiabilityData): Promise<any> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  // Construct the payload exactly as the backend controller expects
  const payload = {
    id: liability.id || null, 
    status: liability.status || 'active',
    request_data: {
      liabilityOwnerType: liability.liabilityOwnerType,
      liabilityType: liability.liabilityType,
      liabilitySubType: liability.liabilitySubType,
      liabilityStartDate: liability.liabilityStartDate,
      liabilityEndDate: liability.liabilityEndDate,
      liabilityTenure: liability.liabilityTenure,
      liabilityTotalAmount: liability.liabilityTotalAmount,
      liabilityOutstandingAmount: liability.liabilityOutstandingAmount,
      liabilityInterestRate: liability.liabilityInterestRate,
      liabilityInstalmentAmount: liability.liabilityInstalmentAmount,
      liabilityFrequency: liability.liabilityFrequency,
      familyMemberId: liability.familyMemberId,
    }
  };
  
  if (payload.request_data.familyMemberId === '' || payload.request_data.familyMemberId === undefined) {
      delete payload.request_data.familyMemberId;
  }

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/liabilities`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error saving liability record:`, error);
    throw error;
  }
};

/**
 * Soft deletes a liability record by its ID via your Node.js backend.
 */
export const deleteLiabilityRecord = async (id: string): Promise<{ success: boolean; message: string }> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/liabilities/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return { success: true, message: result.message };
  } catch (error: any) {
    console.error('Error deleting liability record:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to delete liability record.' 
    };
  }
};